import { ArrowLeft, ArrowRight, BadgeCheck, Calculator, Check, CircleDot, Mail, ShieldCheck } from 'lucide-react'
import { type FormEvent, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { BrandMark } from '../../components/BrandMark'
import { requestJson } from '../../lib/dawnApi'

const recipient = 'sales@deusintus.com'
const formatter = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 })

type Profile = 'personal' | 'business'
type Stage = 'intake' | 'estimate' | 'allocation' | 'pricing' | 'pledge' | 'complete'
type CheckoutState = 'idle' | 'creating' | 'capturing' | 'awaiting-webhook' | 'active' | 'cancelled' | 'failed'

type Assessment = {
  profile: Profile
  name: string
  email: string
  organisation: string
  goal: string
  targetAmount: number
  householdSpend: number
  subscriptions: number
  utilities: number
  telecoms: number
  insurance: number
  software: number
  toolSpend: number
  duplicateSpend: number
  teamWorkflowCost: number
  adminInefficiency: string
  workforceEnhancement: string
  notes: string
  charityPreference: string
}

type Allocation = { goal: number; platform: number; charity: number }

const personalPlans = [
  { id: 'founding-monthly', name: 'Founding 200 — monthly', price: '£9/month', detail: '£108 over 12 months · founding price locked for 12 months', cost: 108, charge: 9 },
  { id: 'founding-annual', name: 'Founding 200 — annual upfront', price: '£90 upfront', detail: '£18 less than 12 monthly payments · founding price locked for 12 months', cost: 90, charge: 90 },
  { id: 'savings-share', name: 'Verified savings share', price: 'Eligibility review', detail: 'Calculated only from verified direct savings under agreed terms', cost: null, charge: null },
  { id: 'hybrid', name: 'Hybrid', price: 'Eligibility review', detail: 'A fixed fee plus a lower verified-savings share under agreed terms', cost: null, charge: null },
] as const

const businessPlans = [
  { id: 'business-audit', name: 'Evidence-based business audit', price: 'from £99', detail: 'A baseline, prioritised opportunities and an evidence plan', cost: null, charge: null },
  { id: 'business-managed', name: 'Managed implementation', price: 'from £1,500', detail: 'Scoped implementation, workforce enhancement and governed rollout', cost: null, charge: null },
  { id: 'business-share', name: 'Verified savings share', price: 'Eligibility review', detail: 'Available only where direct savings can be evidenced and verified', cost: null, charge: null },
] as const

const primaryLedgerStates = ['DISCOVERED', 'ESTIMATED', 'USER_APPROVED', 'IMPLEMENTED', 'VERIFIED', 'SETTLED']
const exceptionLedgerStates = ['REJECTED', 'EXPIRED', 'DISPUTED', 'REVERSED']
const number = (data: FormData, name: string) => Math.max(0, Number(data.get(name)) || 0)
const checkoutStorageKey = 'dawn-paypal-sandbox-checkout'

type CheckoutSession = {
  journeyId: string
  orderId?: string
  assessment: Assessment
  route: string
  allocation: Allocation
  projectedSavings: { three: number[]; six: number[]; twelve: number[] }
}

type PaymentStatus = {
  orderId: string
  paymentState: string
  providerConfirmationState: string
  activationState: string
  active: boolean
}

export function RequestDemoPage() {
  const initialProfile = new URLSearchParams(window.location.search).get('profile') === 'business' ? 'business' : 'personal'
  const [profile, setProfile] = useState<Profile>(initialProfile)
  const [stage, setStage] = useState<Stage>('intake')
  const [assessment, setAssessment] = useState<Assessment | null>(null)
  const [allocation, setAllocation] = useState<Allocation>({ goal: 90, platform: 10, charity: 0 })
  const [route, setRoute] = useState(initialProfile === 'business' ? 'business-audit' : 'founding-monthly')
  const [checkoutState, setCheckoutState] = useState<CheckoutState>('idle')
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus | null>(null)
  const [checkoutError, setCheckoutError] = useState('')

  const projection = useMemo(() => {
    if (!assessment) return null
    const categories = assessment.profile === 'personal'
      ? [
          ['Subscriptions', assessment.subscriptions],
          ['Utilities', assessment.utilities],
          ['Telecoms', assessment.telecoms],
          ['Insurance', assessment.insurance],
          ['Software', assessment.software],
        ] as const
      : [
          ['Tools and software', assessment.toolSpend],
          ['Duplicated subscriptions', assessment.duplicateSpend],
        ] as const
    const addressableMonthlySpend = categories.reduce((sum, [, value]) => sum + value, 0)
    return {
      categories: categories.filter(([, value]) => value > 0).map(([name]) => name),
      three: [0, addressableMonthlySpend * 3],
      six: [0, addressableMonthlySpend * 6],
      twelve: [0, addressableMonthlySpend * 12],
    }
  }, [assessment])

  const allocationTotal = allocation.goal + allocation.platform + allocation.charity
  const plans = profile === 'personal' ? personalPlans : businessPlans
  const selectedPlan = plans.find(plan => plan.id === route) ?? plans[0]
  const checkoutEligible = assessment?.profile === 'personal' && selectedPlan.charge !== null
  const projectedCharity = projection ? projection.twelve[1] * allocation.charity / 100 : 0
  const projectedNet = projection && selectedPlan.cost !== null
    ? [projection.twelve[0] - selectedPlan.cost, projection.twelve[1] - selectedPlan.cost]
    : null

  const changeProfile = (nextProfile: Profile) => {
    setProfile(nextProfile)
    setRoute(nextProfile === 'business' ? 'business-audit' : 'founding-monthly')
  }

  const assess = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const data = new FormData(event.currentTarget)
    setAssessment({
      profile,
      name: String(data.get('name') || ''),
      email: String(data.get('email') || ''),
      organisation: String(data.get('organisation') || ''),
      goal: String(data.get('goal') || ''),
      targetAmount: number(data, 'targetAmount'),
      householdSpend: number(data, 'householdSpend'),
      subscriptions: number(data, 'subscriptions'),
      utilities: number(data, 'utilities'),
      telecoms: number(data, 'telecoms'),
      insurance: number(data, 'insurance'),
      software: number(data, 'software'),
      toolSpend: number(data, 'toolSpend'),
      duplicateSpend: number(data, 'duplicateSpend'),
      teamWorkflowCost: number(data, 'teamWorkflowCost'),
      adminInefficiency: String(data.get('adminInefficiency') || ''),
      workforceEnhancement: String(data.get('workforceEnhancement') || ''),
      notes: String(data.get('notes') || ''),
      charityPreference: String(data.get('charityPreference') || ''),
    })
    setStage('estimate')
  }

  const setAllocationValue = (key: keyof Allocation, value: number) => {
    setAllocation(current => ({ ...current, [key]: Math.min(100, Math.max(0, value || 0)) }))
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const result = params.get('paypal')
    const journeyId = params.get('journey')
    if (!result || !journeyId) return

    const stored = sessionStorage.getItem(checkoutStorageKey)
    if (!stored) {
      setCheckoutState('failed')
      setCheckoutError('This PayPal return could not be matched to the checkout session.')
      return
    }
    let checkout: CheckoutSession
    try {
      checkout = JSON.parse(stored) as CheckoutSession
    } catch {
      setCheckoutState('failed')
      setCheckoutError('The checkout session could not be read.')
      return
    }
    if (checkout.journeyId !== journeyId) {
      setCheckoutState('failed')
      setCheckoutError('The PayPal return did not match this checkout session.')
      return
    }
    setAssessment(checkout.assessment)
    setProfile(checkout.assessment.profile)
    setRoute(checkout.route)
    setAllocation(checkout.allocation)
    setStage('pledge')

    if (result === 'cancelled') {
      setCheckoutState('cancelled')
      setCheckoutError('PayPal checkout was cancelled. No payment was captured and DAWN was not activated.')
      return
    }
    if (result !== 'approved' || !checkout.orderId) {
      setCheckoutState('failed')
      setCheckoutError('The PayPal approval return was incomplete. DAWN was not activated.')
      return
    }

    let stopped = false
    const finishCheckout = async () => {
      try {
        setCheckoutState('capturing')
        let status = await requestJson<PaymentStatus>(`/api/v1/paypal/orders/${encodeURIComponent(checkout.orderId!)}/capture`, {
          method: 'POST',
          body: JSON.stringify({ journeyId }),
        })
        if (!stopped) setPaymentStatus(status)
        for (let attempt = 0; !status.active && attempt < 20; attempt += 1) {
          if (!stopped) setCheckoutState('awaiting-webhook')
          await new Promise(resolve => window.setTimeout(resolve, 1500))
          status = await requestJson<PaymentStatus>(`/api/v1/paypal/orders/${encodeURIComponent(checkout.orderId!)}/status?journeyId=${encodeURIComponent(journeyId)}`)
          if (!stopped) setPaymentStatus(status)
        }
        if (stopped) return
        if (!status.active) throw new Error('PayPal captured the sandbox payment, but independent webhook confirmation has not arrived yet.')
        setCheckoutState('active')
        setStage('complete')
      } catch (error) {
        if (stopped) return
        setCheckoutState('failed')
        setCheckoutError(error instanceof Error ? error.message : 'Sandbox payment confirmation failed. DAWN was not activated.')
      }
    }
    void finishCheckout()
    return () => { stopped = true }
  }, [])

  const pledge = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!assessment || !projection || allocationTotal !== 100) return
    if (checkoutEligible) {
      setCheckoutState('creating')
      setCheckoutError('')
      const journeyId = crypto.randomUUID()
      const checkout: CheckoutSession = {
        journeyId,
        assessment,
        route,
        allocation,
        projectedSavings: { three: projection.three, six: projection.six, twelve: projection.twelve },
      }
      sessionStorage.setItem(checkoutStorageKey, JSON.stringify(checkout))
      try {
        const order = await requestJson<{ orderId: string; approveUrl: string }>('/api/v1/paypal/orders', {
          method: 'POST',
          body: JSON.stringify({
            journeyId,
            planId: route,
            profile: assessment.profile,
            email: assessment.email,
            allocation,
            projectedSavings: checkout.projectedSavings,
          }),
        })
        checkout.orderId = order.orderId
        sessionStorage.setItem(checkoutStorageKey, JSON.stringify(checkout))
        window.location.assign(order.approveUrl)
      } catch (error) {
        setCheckoutState('failed')
        setCheckoutError(error instanceof Error ? error.message : 'PayPal Sandbox checkout could not be started.')
      }
      return
    }
    sessionStorage.setItem('dawn-sandbox-pledge', JSON.stringify({
      truthState: 'PLEDGED',
      ledgerState: 'USER_APPROVED',
      profile: assessment.profile,
      goal: assessment.goal,
      route,
      allocation,
      projectedSavings: { three: projection.three, six: projection.six, twelve: projection.twelve },
    }))
    setStage('complete')
  }

  const emailHref = useMemo(() => {
    if (!assessment || !projection) return `mailto:${recipient}`
    const subject = encodeURIComponent(`DAWN ${assessment.profile} journey pledge — ${assessment.name}`)
    const body = encodeURIComponent([
      paymentStatus?.active ? 'DAWN PayPal Sandbox checkout confirmation' : 'DAWN journey pledge (no payment taken)', '',
      `Profile: ${assessment.profile}`,
      `Name: ${assessment.name}`,
      `Email: ${assessment.email}`,
      `Organisation: ${assessment.organisation || 'Not supplied'}`,
      `Savings goal: ${assessment.goal}`,
      `Target amount: ${assessment.targetAmount ? formatter.format(assessment.targetAmount) : 'Not supplied'}`,
      `Projected 12-month screening range: ${formatter.format(projection.twelve[0])}–${formatter.format(projection.twelve[1])}`,
      `Allocation: goal ${allocation.goal}% / platform ${allocation.platform}% / charity ${allocation.charity}%`,
      `Pricing selection: ${selectedPlan.name} — ${selectedPlan.price}`,
      `Payment state: ${paymentStatus?.paymentState ?? 'PLEDGED'}`,
      `Value ledger state: ${paymentStatus?.active ? 'SETTLED — PERSISTED' : 'USER_APPROVED — browser session only'}`,
    ].join('\n'))
    return `mailto:${recipient}?subject=${subject}&body=${body}`
  }, [allocation, assessment, paymentStatus, projection, selectedPlan])

  const progress = stage === 'intake' ? 1 : stage === 'estimate' ? 2 : stage === 'allocation' ? 3 : stage === 'pricing' ? 4 : stage === 'pledge' ? 5 : 6

  return (
    <section className="form-page">
      <div className="container form-page__grid">
        <div className="form-page__intro">
          <Link to="/" aria-label="Return to DAWN home"><BrandMark light /></Link>
          <span className="form-page__eyebrow">DAWN savings journey</span>
          <h1>Find what you could save.</h1>
          <p>Enter manual figures, review a transparent screening estimate, decide how future verified savings should be allocated and choose a pricing route.</p>
          <ul className="check-list">
            <li><Check size={17} />No banking login, card or account details</li>
            <li><Check size={17} />Projected and verified savings stay separate</li>
            <li><Check size={17} />No cancellation or switch without your approval</li>
          </ul>
          <div className="journey-progress" aria-label={`Step ${progress} of 6`}>
            <span>Step {progress} of 6</span><i><b style={{ width: `${progress / 6 * 100}%` }} /></i>
          </div>
          <a className="pilot-email-link" href={`mailto:${recipient}`}><Mail size={17} />Prefer help? Contact {recipient}</a>
        </div>

        {stage === 'intake' && <form className="demo-form" onSubmit={assess}>
          <div className="form-step"><span>1</span><div><strong>Your starting point</strong><small>Manual figures only — do not enter banking credentials</small></div></div>
          <fieldset>
            <legend>Who is this for?</legend>
            <div className="profile-switch">
              <button className={profile === 'personal' ? 'active' : ''} type="button" onClick={() => changeProfile('personal')}>Personal</button>
              <button className={profile === 'business' ? 'active' : ''} type="button" onClick={() => changeProfile('business')}>Business</button>
            </div>
          </fieldset>
          <div className="form-row">
            <label>Name<input name="name" required autoComplete="name" /></label>
            <label>Email<input name="email" required type="email" autoComplete="email" /></label>
          </div>
          <label>{profile === 'business' ? 'Business name' : 'Organisation (optional)'}<input name="organisation" required={profile === 'business'} autoComplete="organization" /></label>
          <label>{profile === 'business' ? 'What outcome should DAWN help your business achieve?' : 'What are you saving for?'}
            <textarea name="goal" required rows={3} placeholder={profile === 'business' ? 'For example: reduce software waste and give the team more useful capacity' : 'Your savings goal or dream'} />
          </label>
          <label>Target amount, if known (£)<input name="targetAmount" type="number" min="0" step="1" inputMode="decimal" /></label>

          {profile === 'personal' ? <>
            <label>Current monthly household spend (£)<input name="householdSpend" required type="number" min="0" step="1" inputMode="decimal" /></label>
            <div className="form-section-label">Monthly recurring costs to screen</div>
            <div className="form-row form-row--numbers">
              <label>Subscriptions (£)<input name="subscriptions" type="number" min="0" step="1" inputMode="decimal" /></label>
              <label>Utilities (£)<input name="utilities" type="number" min="0" step="1" inputMode="decimal" /></label>
              <label>Telecoms (£)<input name="telecoms" type="number" min="0" step="1" inputMode="decimal" /></label>
              <label>Insurance (£)<input name="insurance" type="number" min="0" step="1" inputMode="decimal" /></label>
            </div>
            <label>Software and apps (£/month)<input name="software" type="number" min="0" step="1" inputMode="decimal" /></label>
          </> : <>
            <div className="form-row form-row--numbers">
              <label>Tools and software (£/month)<input name="toolSpend" required type="number" min="0" step="1" inputMode="decimal" /></label>
              <label>Known duplicated subscriptions (£/month)<input name="duplicateSpend" type="number" min="0" step="1" inputMode="decimal" /></label>
            </div>
            <label>Team and workflow costs (£/month, context only)<input name="teamWorkflowCost" type="number" min="0" step="1" inputMode="decimal" /><small>Not converted into a cash-saving claim.</small></label>
            <label>Where does admin or process work get stuck?<textarea name="adminInefficiency" rows={3} placeholder="Repeated entry, chasing, handoffs, reporting or other bottlenecks" /></label>
            <label>Where should DAWN enhance the workforce?<textarea name="workforceEnhancement" rows={3} placeholder="Capacity, quality, consistency or service improvements — not assumed job replacement" /></label>
          </>}

          <label>What would you most like help reducing? (optional)<textarea name="notes" rows={3} /></label>
          <label>Charity preference (optional)<input name="charityPreference" placeholder="A cause or registered charity you care about" /></label>
          <button className="button button--gold" type="submit">Calculate projected savings <ArrowRight size={17} /></button>
          <small className="pilot-form-note">Your answers stay in this browser unless you choose to email them.</small>
        </form>}

        {stage === 'estimate' && assessment && projection && <div className="demo-form">
          <button className="form-back" type="button" onClick={() => setStage('intake')}><ArrowLeft size={15} />Change my figures</button>
          <div className="form-step"><span>2</span><div><strong>PROJECTED SAVINGS</strong><small>Screening estimate — not verified savings</small></div></div>
          <div className="projection-card">
            <Calculator size={22} />
            <span>12-month projected screening range</span>
            <strong>{formatter.format(projection.twelve[0])}–{formatter.format(projection.twelve[1])}</strong>
            <small>The lower bound is £0 because no invoice, contract or completed saving has been verified. The upper bound is the recurring spend you entered as addressable; it is a screening ceiling, not a forecast or guarantee.</small>
          </div>
          <div className="range-grid">
            <article><small>3 months</small><strong>{formatter.format(projection.three[0])}–{formatter.format(projection.three[1])}</strong></article>
            <article><small>6 months</small><strong>{formatter.format(projection.six[0])}–{formatter.format(projection.six[1])}</strong></article>
            <article><small>12 months</small><strong>{formatter.format(projection.twelve[0])}–{formatter.format(projection.twelve[1])}</strong></article>
          </div>
          <div className="assumption-card">
            <strong>Confidence: low until evidence is reviewed</strong>
            <p>Possible categories: {projection.categories.length ? projection.categories.join(', ') : 'none entered yet'}. DAWN would next classify each item as KEEP, REPLACE, CONSOLIDATE, REMOVE, DOWNGRADE, LOCALISE or RENEGOTIATE.</p>
            <p>Projected savings are estimates. Verified savings require baseline evidence, your approval, an implemented change and a lower cost that persists through an agreed review period.</p>
          </div>
          <div className="ledger-preview">
            <span>Value Ledger preview</span>
            <div>{primaryLedgerStates.map(state => <i className={state === 'ESTIMATED' ? 'active' : ''} key={state}>{state}</i>)}</div>
            <small>Current state: ESTIMATED · browser session only</small>
          </div>
          <button className="button button--gold" type="button" onClick={() => setStage('allocation')}>Choose my allocation <ArrowRight size={17} /></button>
        </div>}

        {stage === 'allocation' && assessment && projection && <div className="demo-form">
          <button className="form-back" type="button" onClick={() => setStage('estimate')}><ArrowLeft size={15} />Back to estimate</button>
          <div className="form-step"><span>3</span><div><strong>Choose your savings allocation</strong><small>A visible planning preference — no money is moved</small></div></div>
          <div className="allocation-list">
            <label><span><strong>Your savings goal</strong><small>{assessment.goal}</small></span><input aria-label="Your savings goal percentage" type="number" min="0" max="100" value={allocation.goal} onChange={event => setAllocationValue('goal', Number(event.target.value))} /><b>%</b></label>
            <label><span><strong>Deus / platform contribution</strong><small>Planning preference only; actual fees follow your selected plan.</small></span><input aria-label="Platform contribution percentage" type="number" min="0" max="100" value={allocation.platform} onChange={event => setAllocationValue('platform', Number(event.target.value))} /><b>%</b></label>
            <label><span><strong>Charity contribution</strong><small>{assessment.charityPreference || 'No charity selected yet'}</small></span><input aria-label="Charity contribution percentage" type="number" min="0" max="100" value={allocation.charity} onChange={event => setAllocationValue('charity', Number(event.target.value))} /><b>%</b></label>
          </div>
          <div className={`allocation-total ${allocationTotal === 100 ? 'valid' : ''}`}><span>Total</span><strong>{allocationTotal}%</strong><small>{allocationTotal === 100 ? 'Ready to continue' : 'Adjust the split so it totals 100%'}</small></div>
          <div className="pledge-note"><ShieldCheck size={22} /><div><strong>No automatic contribution or charity transfer</strong><p>These percentages describe what you would like to happen after value is verified. Any charge, donation or consequential action needs separate confirmation and evidence.</p></div></div>
          <button className="button button--gold" type="button" disabled={allocationTotal !== 100} onClick={() => setStage('pricing')}>Review pricing <ArrowRight size={17} /></button>
        </div>}

        {stage === 'pricing' && assessment && projection && <div className="demo-form">
          <button className="form-back" type="button" onClick={() => setStage('allocation')}><ArrowLeft size={15} />Back to allocation</button>
          <div className="form-step"><span>4</span><div><strong>{profile === 'personal' ? 'Founding 200 pricing' : 'Business pricing routes'}</strong><small>Choose a route before the no-charge pledge</small></div></div>
          {profile === 'personal' && <div className="founding-banner"><span>First 200 customers</span><strong>£9/month or £90 upfront</strong><small>Expected future base price: around £49/month, depending on capabilities used.</small></div>}
          <fieldset>
            <legend>Select a pricing route</legend>
            <div className="pricing-choice-list">
              {plans.map(plan => <label className="pricing-choice" key={plan.id}>
                <input type="radio" name="route" value={plan.id} checked={route === plan.id} onChange={() => setRoute(plan.id)} />
                <span><strong>{plan.name}<small>{plan.detail}</small></strong><b>{plan.price}</b></span>
              </label>)}
            </div>
          </fieldset>
          {profile === 'personal' && <div className="price-metrics">
            <article><small>Monthly option</small><strong>£9/month</strong><span>£108 across 12 months</span></article>
            <article><small>Annual upfront</small><strong>£90</strong><span>£18 upfront discount</span></article>
            <article><small>Against expected £49 base</small><strong>{route === 'founding-annual' ? 'about £498' : route === 'founding-monthly' ? 'about £480' : 'Not calculated'}</strong><span>Approximate first-year price difference</span></article>
          </div>}
          <div className="assumption-card">
            <strong>Projected customer net benefit after DAWN cost</strong>
            <p>{projectedNet ? `${formatter.format(projectedNet[0])}–${formatter.format(projectedNet[1])} over 12 months using the screening range and selected fixed price.` : 'Not calculated for an eligibility-review or quoted route. No fee may be calculated from unverified savings.'}</p>
            <p>Projected charity contribution at the upper screening bound: {formatter.format(projectedCharity)}. This is not a transfer, donation or accrued amount.</p>
          </div>
          {profile === 'personal' && <p className="price-lock-copy">Your founding price is locked for 12 months. After that, pricing may move gradually toward the standard rate. You will be told in advance and can cancel.</p>}
          <p className="estimate-note">Savings-share and hybrid terms are never calculated from projected savings. Eligibility, amount and duration require verified direct savings and a separate agreement.</p>
          <button className="button button--gold" type="button" onClick={() => setStage('pledge')}>Continue to checkout status <ArrowRight size={17} /></button>
        </div>}

        {stage === 'pledge' && assessment && projection && <form className="demo-form" onSubmit={pledge}>
          <button className="form-back" type="button" onClick={() => setStage('pricing')}><ArrowLeft size={15} />Back to pricing</button>
          <div className="form-step"><span>5</span><div><strong>Checkout status</strong><small>Exact payment truth before you continue</small></div></div>
          <div className="truth-state"><CircleDot size={18} /><div><span>PAYMENT STATUS</span><strong>{checkoutEligible ? (
            checkoutState === 'capturing' ? 'SANDBOX · SERVER CAPTURE IN PROGRESS'
              : checkoutState === 'awaiting-webhook' ? 'SANDBOX · AWAITING VERIFIED WEBHOOK'
                : checkoutState === 'active' ? 'SANDBOX · PAYMENT CONFIRMED'
                  : checkoutState === 'cancelled' ? 'SANDBOX · CHECKOUT CANCELLED'
                    : checkoutState === 'failed' ? 'SANDBOX · NOT ACTIVATED'
                      : 'SANDBOX · READY FOR PAYPAL APPROVAL'
          ) : 'PLEDGED · ELIGIBILITY REVIEW REQUIRED'}</strong></div></div>
          {checkoutEligible
            ? <div className="pledge-note"><ShieldCheck size={22} /><div><strong>PayPal Sandbox only</strong><p>DAWN will create and capture the order on the server. Activation occurs only after the amount, currency and order identity pass server checks and a verified PayPal webhook independently confirms the capture. No real money is used in Sandbox.</p></div></div>
            : <div className="pledge-note"><ShieldCheck size={22} /><div><strong>No payment will be taken</strong><p>This pricing route requires an eligibility review or a separate quote, so it remains a non-binding browser-session pledge. Your DAWN account is not activated.</p></div></div>}
          {checkoutError && <p className="estimate-note" role="alert">{checkoutError}</p>}
          <dl className="review-list">
            <div><dt>Profile</dt><dd>{assessment.profile}</dd></div>
            <div><dt>Plan</dt><dd>{selectedPlan.name} · {selectedPlan.price}</dd></div>
            <div><dt>Goal</dt><dd>{assessment.goal}</dd></div>
            <div><dt>Projected 12-month range</dt><dd>{formatter.format(projection.twelve[0])}–{formatter.format(projection.twelve[1])}</dd></div>
            <div><dt>Allocation</dt><dd>{allocation.goal}% goal · {allocation.platform}% platform · {allocation.charity}% charity</dd></div>
          </dl>
          <label className="checkbox-label"><input required type="checkbox" />{checkoutEligible
            ? `I understand this is a PayPal Sandbox test for £${selectedPlan.charge?.toFixed(2)} GBP; no real money is used, and approval alone does not activate DAWN.`
            : 'I understand this is a no-charge, non-binding pledge; no payment, account activation, cancellation, switch or charity transfer occurs.'}</label>
          <button className="button button--gold" type="submit" disabled={['creating', 'capturing', 'awaiting-webhook'].includes(checkoutState)}>{checkoutEligible
            ? checkoutState === 'creating' ? 'Creating secure Sandbox order…' : 'Continue to PayPal Sandbox'
            : 'Record pledge and see next step'} <ArrowRight size={17} /></button>
        </form>}

        {stage === 'complete' && assessment && projection && <div className="demo-form confirmation-card" role="status">
          <BadgeCheck size={38} />
          <span className="confirmation-card__status">{paymentStatus?.active ? 'Sandbox payment confirmed' : 'Journey pledge recorded'}</span>
          <h2>Your DAWN starting point is ready.</h2>
          <p>{paymentStatus?.active
            ? 'PayPal Sandbox capture and the independently verified webhook both succeeded. This test activation is persisted in the Value Ledger; no real money was used.'
            : 'No payment was taken and no live account was activated. Your selection is held only in this browser session.'}</p>
          <dl>
            <div><dt>Selected plan</dt><dd>{selectedPlan.name} · {selectedPlan.price}</dd></div>
            <div><dt>Savings goal</dt><dd>{assessment.goal}</dd></div>
            <div><dt>Projected savings</dt><dd>{formatter.format(projection.twelve[0])}–{formatter.format(projection.twelve[1])} / 12 months</dd></div>
            <div><dt>Chosen allocation</dt><dd>{allocation.goal}% / {allocation.platform}% / {allocation.charity}%</dd></div>
            <div><dt>Account / journey state</dt><dd>{paymentStatus?.active ? 'SANDBOX · ACTIVATED' : 'PLEDGED · NOT ACTIVATED'}</dd></div>
            <div><dt>Value Ledger state</dt><dd>{paymentStatus?.active ? 'PAYMENT_CONFIRMED · PERSISTED' : 'USER_APPROVED · SESSION ONLY'}</dd></div>
            <div><dt>Next inspection</dt><dd>{profile === 'business' ? 'Tool invoices, duplicate subscriptions and one workflow baseline' : 'Recurring bills, renewal dates and approved comparison criteria'}</dd></div>
          </dl>
          <div className="ledger-preview ledger-preview--complete">
            <span>Verified Savings / Value Ledger</span>
            <div>{primaryLedgerStates.map(state => <i className={state === (paymentStatus?.active ? 'SETTLED' : 'USER_APPROVED') ? 'active' : ''} key={state}>{state}</i>)}</div>
            <small>Preserved exception states: {exceptionLedgerStates.join(' · ')}</small>
          </div>
          <a className="button button--gold" href={emailHref}><Mail size={16} />Send my request to DAWN</a>
          <Link className="button button--outline" to="/onboarding">Explore the onboarding preview <ArrowRight size={17} /></Link>
          <small className="pilot-form-note">Next action: send your request if you want a human-reviewed evidence baseline. Consequential changes always require your approval.</small>
        </div>}
      </div>
    </section>
  )
}
